/**
 * DataSourceContextPane - what this data source IS, in three glances.
 *
 *   1. Overview    status, indexer, type, language, visibility, size - CHIPS
 *   2. At a glance the two counts worth pressing, as pressable stat tiles
 *   3. Timeline    created / updated / last indexed / last AI analysis
 *
 * WHAT THIS PANE STOPPED BEING. It was five stacked `GlassCard`s - State,
 * Contents, Timeline, Keywords, Supported Formats - each with its own header
 * row and its own inner `FieldGrid` of `FactTile`s, which are themselves glass
 * cards. Boxes inside boxes inside a box, in a column that starts at 260px, so
 * a two-word fact like "Language: English" got a header, a border and a tile of
 * its own. Everything heavy - the keyword list, the format list, the numbers —
 * is on the Details CANVAS now, where there is room for it; see
 * `DataSourceDetailsPanel`. What is left here is what a context pane is for:
 * identity you read without clicking.
 *
 * The vocabulary is the shared one (`shared/brand/ContextPane`), so this pane
 * reads exactly like the matter and review context panes rather than
 * re-deriving its own 10px label.
 *
 * NOTHING IS FETCHED HERE. Every figure comes from the record the page already
 * holds, so opening or closing this pane costs no request.
 */

import React from 'react';
import {
  PiDatabaseLight,
  PiDiamondsFourLight,
  PiEyeLight,
  PiFileLight,
  PiGlobeLight,
  PiTagLight,
} from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../../../../utils/functions';
import Badge from '../../../shared/TailwindComponents/Badge';
import BrandLoader from '../../../shared/brand/BrandLoader';
import GlassCard from '../../../shared/brand/GlassCard';
import {
  Chip,
  ContextSection,
  StatGrid,
  StatTile,
  TimelineRail,
  TimelineRow,
} from '../../../shared/brand/ContextPane';
import {
  DataSource,
  formatSize,
  indexerCfg,
  parseFileTypes,
  statusCfg,
  visibilityLabel,
} from '../DataSourceDetailsPanel';
import { useDataSourceSurface } from './DataSourceSurfaceContext';

interface Props {
  dataSource?: DataSource | null;
  /**
   * A reindex queued by a file delete is still running.
   *
   * The page owns this - it is driven by the live socket subscription, which
   * must not be tied to whether this pane happens to be open.
   */
  isReindexing?: boolean;
}

const DataSourceContextPane: React.FC<Props> = ({ dataSource: ds, isReindexing }) => {
  const { t } = useTranslation();
  /* Null outside the surface, so the tiles simply stop being pressable rather
     than throwing - the same contract every other context pane has. */
  const surface = useDataSourceSurface();

  const st = statusCfg(ds?.status);
  const ix = indexerCfg(ds?.indexer_status || ds?.indexing_status);
  const fileTypes = parseFileTypes(ds?.allowed_file_types);
  const keywordCount = ds?.keywords?.length ?? 0;
  const indexerRunning =
    ds?.indexer_status === 'running' || ds?.indexing_status === 'running';

  const goTo = (tab: string) => (surface ? () => surface.goToTab(tab) : undefined);

  /* `formatDate`/`formatDateTime` answer "-" for a missing date, which is
     truthy - a `TimelineRow` handed that would light its dot and print a
     stray hyphen instead of reading as "not yet". Absent stays undefined. */
  const day = (v?: string | null) => (v ? formatDate(v) : undefined);
  const stamp = (v?: string | null) => (v ? formatDateTime(v) : undefined);

  return (
    <div data-testid="datasource-context-pane" className="p-3 space-y-5">
      {/* ── 1. Overview: every identity fact, one wrapping chip row ── */}
      <section data-tour-id="ds-context-overview">
        <ContextSection>{t('Overview')}</ContextSection>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge color={st.badge as any} className="!text-[11px] !px-2 !py-0.5">
            {t(st.label)}
          </Badge>
          <Chip
            title={t('Job status')}
            icon={
              indexerRunning || isReindexing ? (
                <BrandLoader size="xs" className="!h-3 !w-3" />
              ) : undefined
            }
          >
            {t(ix.label)}
          </Chip>
          {ds?.type && (
            <Chip icon={<PiDiamondsFourLight />} title={t('Type')}>{ds.type}</Chip>
          )}
          {ds?.language && (
            <Chip icon={<PiGlobeLight />} title={t('Language')}>{ds.language}</Chip>
          )}
          <Chip icon={<PiEyeLight />} title={t('Visibility')}>
            {t(visibilityLabel(ds || undefined))}
          </Chip>
          <Chip icon={<PiDatabaseLight />} title={t('Total Size')}>
            {formatSize(ds?.total_size_bytes)}
          </Chip>
        </div>

        {isReindexing && (
          <p className="mt-2 text-[11px] text-fg-muted">
            {t('Reprocessing data source… This may take a moment.')}
          </p>
        )}
      </section>

      {/* ── 2. At a glance: counts that navigate ── */}
      <section>
        <ContextSection>{t('At a glance')}</ContextSection>
        <GlassCard padding="none">
          <StatGrid>
            <StatTile
              icon={<PiFileLight />}
              label={t('Documents')}
              value={ds?.document_count ?? 0}
              onClick={goTo('Files')}
            />
            <StatTile
              icon={<PiTagLight />}
              label={t('Keywords')}
              value={keywordCount}
              onClick={goTo('Details')}
            />
            <StatTile
              icon={<PiFileLight />}
              label={t('Formats')}
              value={fileTypes.length}
              onClick={goTo('Details')}
            />
          </StatGrid>
        </GlassCard>
      </section>

      {/* ── 3. Timeline ── */}
      <section>
        <ContextSection>{t('Timeline')}</ContextSection>
        <TimelineRail>
          <TimelineRow label={t('Created')} value={day(ds?.created_at || ds?.CreatedAt)} />
          <TimelineRow label={t('Updated')} value={stamp(ds?.updated_at)} />
          <TimelineRow
            label={t('Last processed')}
            value={stamp(ds?.last_indexed_at || ds?.last_index_run)}
          />
          <TimelineRow label={t('AI analysis')} value={stamp(ds?.llm_generated_at)} />
        </TimelineRail>
      </section>
    </div>
  );
};

export default DataSourceContextPane;
